/**
 * Maison scroll reveal (content-only).
 * Usage: add `data-maison-reveal` to any element you want to fade in on scroll.
 * Optional: set `style="--maison-reveal-order: N"` for staggered delays.
 */
(function () {
    const VISIBLE_CLASS = 'maison-reveal--visible';
    const SELECTOR = '[data-maison-reveal]';
    const SCOPE_SELECTOR = '[data-maison-reveal-scope]';
  
    // Tunables via CSS variables on :root (or any ancestor, e.g. section wrapper):
    //   --maison-reveal-root-margin-desktop: 0px 0px 20% 0px;
    //   --maison-reveal-root-margin-mobile:  0px 0px 30% 0px;
    const DEFAULT_ROOT_MARGIN_DESKTOP = '0px 0px 20% 0px';
    const DEFAULT_ROOT_MARGIN_MOBILE = '0px 0px 30% 0px';
    const MOBILE_MQ = '(max-width: 749px)';
  
    function readCssVar(name, el) {
      const target = el && el.nodeType === 1 ? el : document.documentElement;
      return (getComputedStyle(target).getPropertyValue(name) || '').trim();
    }
  
    function getObserverOptions(scopeEl) {
      const isMobile = window.matchMedia && window.matchMedia(MOBILE_MQ).matches;
      const rootMargin = isMobile
        ? (readCssVar('--maison-reveal-root-margin-mobile', scopeEl) || DEFAULT_ROOT_MARGIN_MOBILE)
        : (readCssVar('--maison-reveal-root-margin-desktop', scopeEl) || DEFAULT_ROOT_MARGIN_DESKTOP);
  
      return { threshold: 0, rootMargin };
    }
  
    const observed = new WeakSet();
    const scopedObservers = new WeakMap();
    let globalObserver = null;
  
    function afterNextPaint(fn) {
      // Two RAFs ensures at least one paint happens before we mutate classes.
      requestAnimationFrame(() => requestAnimationFrame(fn));
    }
  
    function reveal(el) {
      el.classList.add(VISIBLE_CLASS);
    }
  
    function createObserver(options) {
      if (!('IntersectionObserver' in window)) return null;
      return new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          // Defer class mutation so CSS transitions reliably run (especially above the fold).
          setTimeout(() => reveal(entry.target), 0);
          obs.unobserve(entry.target);
        });
      }, options);
    }
  
    function getObserverForScope(scopeEl) {
      if (!scopeEl) {
        if (globalObserver) return globalObserver;
        globalObserver = createObserver(getObserverOptions(document.documentElement));
        return globalObserver;
      }
  
      const existing = scopedObservers.get(scopeEl);
      if (existing) return existing;
  
      const obs = createObserver(getObserverOptions(scopeEl));
      if (obs) scopedObservers.set(scopeEl, obs);
      return obs;
    }
  
    function observeElements(els, scopeEl) {
      if (!els || !els.length) return;
      const io = getObserverForScope(scopeEl);
      if (!io) {
        return afterNextPaint(() => els.forEach(reveal));
      }
  
      // Wait until after first paint so initial hidden styles are actually painted.
      afterNextPaint(() => {
        els.forEach((el) => {
          if (observed.has(el)) return;
          observed.add(el);
          io.observe(el);
        });
      });
    }
  
    function init(root) {
      const scope = root && root.querySelectorAll ? root : document;
  
      // If we're initializing the whole document, reveal per-scope first so sections
      // don't "share" a single observer/timeline unintentionally.
      if (scope === document) {
        const scopes = Array.from(document.querySelectorAll(SCOPE_SELECTOR));
        scopes.forEach((s) => init(s));
  
        // Then handle any reveal elements that are NOT inside a scope.
        const globalEls = Array.from(document.querySelectorAll(SELECTOR)).filter(
          (el) => !el.closest(SCOPE_SELECTOR)
        );
        observeElements(globalEls, null);
        return;
      }
  
      // If this root itself is a scope, keep it isolated.
      const scopeEl = scope.matches && scope.matches(SCOPE_SELECTOR) ? scope : scope.closest(SCOPE_SELECTOR);
      const els = Array.from(scope.querySelectorAll(SELECTOR));
      observeElements(els, scopeEl || null);
    }
  
    function onReady(fn) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', fn, { once: true });
      } else {
        fn();
      }
    }
  
    onReady(() => afterNextPaint(() => init(document)));
    window.addEventListener('load', () => init(document), { once: true });
  
    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:load', (event) => init(event.target));
      document.addEventListener('shopify:section:reorder', () => init(document));
    }
  })();
  
  